import React, { useEffect, useCallback, useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import {
  DataGrid,
  GridToolbar,
  GridRowSelectionModel,
  GridValidRowModel,
  GridCallbackDetails,
  useGridApiRef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { isEmpty } from "lodash";

import { Tag } from "../../components/tag/tag";
import AlertModal from "../../components/alert-modal/alert-modal";

import { useGetUsersQuery } from "../../graphql/queries/getUsers.generated";
import { useCreateSystemNotificationsMutation } from "../../graphql/mutations/createSystemNotifications.generated";

import mobileRouteNames from "../../constants/mobile-route-names";
import routeNames from "../../constants/route-names";
import { useAuth } from "../../context/AuthContext";
import Colors from "../../styles/colors";
import "./create-notification.css";
import { InputMaybe, Users_Bool_Exp } from "../../graphql/generated/graphql";

const CreateNotification = () => {
  const dataGridApiRef = useGridApiRef();
  const navigate = useNavigate();
  const { loggedUserData } = useAuth();

  const [selectedUsersIds, setSelectedUsersIds] =
    useState<GridRowSelectionModel>([]);
  const [selectedUsers, setSelectedUsers] = useState<GridValidRowModel>([]);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationMessageError, setNotificationMessageError] =
    useState<string>("");
  const [notificationPayload, setNotificationPayload] = useState<string>("");
  const [notificationPayloadError, setNotificationPayloadError] =
    useState<string>("");
  const [notificationPayloadRequired, setNotificationPayloadRequired] =
    useState<string>("");
  const [successCount, setSuccessCount] = useState<number>(0);
  const [failuresCount, setFailuresCount] = useState<number>(0);
  const [resultModalIsOpen, setResultModalIsOpen] = useState<boolean>(false);
  const [isHighnoteAdmin, setIsHighnoteAdmin] = useState<boolean>(false);
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState<boolean>(false);
  const [workspaceId, setWorkspaceId] = useState<number>(0);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      id: false,
      workspace: false,
    });

  const handleOnSelectUser = useCallback(
    (
      newRowSelectionModel: GridRowSelectionModel,
      details: GridCallbackDetails<any>
    ) => {
      const selectedRows = details.api.getSelectedRows();

      const selectedUsers = Array.from(selectedRows, ([key, value]) => {
        return value;
      });

      setSelectedUsersIds(newRowSelectionModel);
      setSelectedUsers(selectedUsers);
    },
    []
  );

  const navigateToHome = useCallback(() => {
    navigate(routeNames.HOME);
  }, []);

  const handleOnRemoveSelectedUser = useCallback(
    (id: string) => {
      if (!isEmpty(dataGridApiRef.current)) {
        dataGridApiRef.current.selectRow(id, false);
      }
    },
    [dataGridApiRef]
  );

  const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
    console.log("Error Get Users: ", error);
  }, []);

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }

    if (loggedUserData?.isHighnoteAdmin) {
      setIsHighnoteAdmin(loggedUserData.isHighnoteAdmin);
    }

    if (loggedUserData?.isWorkspaceAdmin && !loggedUserData?.isHighnoteAdmin) {
      setIsWorkspaceAdmin(true);
    } else {
      setIsWorkspaceAdmin(false);
    }
  }, [loggedUserData]);

  const usersFilter = useMemo(() => {
    const where: InputMaybe<Users_Bool_Exp> | undefined = {};
    if (isHighnoteAdmin) {
      return where;
    }

    if (workspaceId) {
      where.workspaces = { workspaceId: { _eq: workspaceId } };
    }

    return where;
  }, [workspaceId, isHighnoteAdmin]);

  const { data, loading, refetch } = useGetUsersQuery({
    variables: {
      where: usersFilter,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
    onCompleted: (data) => console.log("Get Users data: ", data),
  });

  const resetState = useCallback(() => {
    setSelectedUsersIds([]);
    setSelectedUsers([]);
    setDeepLink(null);
    setNotificationMessage("");
    setNotificationMessageError("");
    setNotificationPayload("");
    setNotificationPayloadError("");
    setNotificationPayloadRequired("");
    setSuccessCount(0);
    setFailuresCount(0);
    setResultModalIsOpen(false);
    if (!isEmpty(dataGridApiRef.current)) {
      dataGridApiRef.current.setRowSelectionModel([]);
    }
    window.scrollTo(0, 0);

    refetch();
  }, [dataGridApiRef, refetch]);

  const handleOnCompletedCreateSystemNotifications = useCallback(
    (data: any) => {
      if (data.newSystemNotifications?.successCount) {
        setSuccessCount(data.newSystemNotifications?.successCount);
      }

      if (
        data.newSystemNotifications &&
        data.newSystemNotifications?.failuresCount
      ) {
        setFailuresCount(data.newSystemNotifications.failuresCount);
      }

      setResultModalIsOpen(true);
    },
    []
  );

  const handleOnErrorCreateSystemNotifications = useCallback(
    (error: ApolloError) => {
      console.log("Mutation Error Create System Notifications: ", error);
    },
    []
  );

  const [
    createSystemNotifications,
    { loading: createSystemNotificationsLoading },
  ] = useCreateSystemNotificationsMutation({
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedCreateSystemNotifications,
    onError: handleOnErrorCreateSystemNotifications,
  });

  const handleSend = useCallback(async () => {
    if (notificationMessage) {
      const targetUsersString = JSON.stringify(selectedUsers);

      createSystemNotifications({
        variables: {
          targetUsers: targetUsersString,
          notificationMessage,
          deepLink,
          payload: notificationPayload,
        },
      });
    }
  }, [
    notificationMessage,
    deepLink,
    selectedUsersIds,
    selectedUsers,
    notificationPayload,
  ]);

  const rows = useMemo(() => {
    if (data?.users) {
      const formattedUsers = data?.users.map((user) => {
        const {
          id,
          firstName,
          lastName,
          role,
          userSpecialty,
          workspaces,
          workspaceLocation,
          fcmToken,
        } = user;

        const workspaceName =
          workspaces && workspaces[0] && workspaces[0].workspace.name
            ? workspaces[0].workspace.name
            : "";

        const workspaceLocationName =
          workspaceLocation && workspaceLocation.name
            ? workspaceLocation.name
            : "";

        const roleName = role && role.name ? role.name : "";

        const specialtyName =
          userSpecialty && userSpecialty.name ? userSpecialty.name : "";

        return {
          id,
          firstName,
          lastName,
          workspace: workspaceName,
          workspaceLocation: workspaceLocationName,
          role: roleName,
          specialty: specialtyName,
          fcmToken,
        };
      });

      return formattedUsers;
    }

    return [];
  }, [data?.users]);

  const columns = useMemo(() => {
    return [
      { field: "id", headerName: "ID", flex: 1.5, minWidth: 100 },
      { field: "firstName", headerName: "First Name", flex: 1, minWidth: 130 },
      { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 130 },
      { field: "workspace", headerName: "Workspace", flex: 1, minWidth: 130 },
      {
        field: "workspaceLocation",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
      },
      { field: "role", headerName: "Role", flex: 1, minWidth: 150 },
      { field: "specialty", headerName: "Specialty", flex: 1, minWidth: 150 },
    ];
  }, []);

  const userTags = useMemo(() => {
    return (
      <div className="tags-container">
        {selectedUsers.map(
          (user: { id: string; firstName: string; lastName: string }) => {
            return (
              <Tag
                key={user.id}
                label={`${user.firstName} ${user.lastName}`}
                onDelete={() => {
                  handleOnRemoveSelectedUser(user.id);
                }}
              />
            );
          }
        )}
      </div>
    );
  }, [selectedUsers, handleOnRemoveSelectedUser]);

  const mobileRouteNamesValues = useMemo(() => {
    if (isWorkspaceAdmin && !isHighnoteAdmin) {
      return [
        { value: "mentorship-dashboard", label: "Mentorship Dashboard" },
        { value: "hignome-chat", label: "HiGnome AI Chat" },
        { value: "grit", label: "GRIT Leaderboard" },
        { value: "discover", label: "Community" },
      ];
    }

    const values = Object.entries(mobileRouteNames).map(([key, value]) => ({
      value: value,
      label: value
    }));

    return values;
  }, [isWorkspaceAdmin, isHighnoteAdmin]);

  const handleSelectAllUsers = useCallback(() => {
    const allRowIds = rows.map((row) => row.id);

    if (selectedUsersIds.length === rows.length) {
      setSelectedUsersIds([]);
      setSelectedUsers([]);
      if (dataGridApiRef.current) {
        dataGridApiRef.current.setRowSelectionModel([]);
      }
    } else {
      setSelectedUsersIds(allRowIds);
      setSelectedUsers(rows);
      if (dataGridApiRef.current) {
        dataGridApiRef.current.setRowSelectionModel(allRowIds);
      }
    }
  }, [rows, selectedUsersIds, dataGridApiRef]);

  return (
    <Container component="main" maxWidth={false}>
      <h1 className="screen-title">Send custom push notifications</h1>
      <h2>1. Select users</h2>
      <Button
        variant="contained"
        size="small"
        onClick={handleSelectAllUsers}
        sx={{
          backgroundColor: Colors.Highnote.blue4,
          ":hover": { backgroundColor: Colors.Highnote.blue3, color: "white" },
        }}
      >
        {selectedUsersIds.length === rows.length
          ? "Deselect All"
          : "Select All"}
      </Button>
      {userTags}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : data?.users.length ? (
        <DataGrid
          apiRef={dataGridApiRef}
          rows={rows}
          columns={columns}
          checkboxSelection={true}
          onRowSelectionModelChange={handleOnSelectUser}
          disableMultipleRowSelection={false}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: Colors.Highnote.blue4,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: Colors.Highnote.white,
              fontWeight: "900",
            },
            "& .MuiDataGrid-cell": {
              border: 0.5,
              borderColor: Colors.Highnote.blue1,
              color: Colors.Highnote.black,
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:hover": {
              border: 1,
              borderColor: Colors.Highnote.blue2,
              color: "primary.main",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: Colors.Highnote.blue4,
              color: Colors.Highnote.white,
              fontWeight: "900",
            },
            "& .MuiTablePagination-toolbar": {
              backgroundColor: Colors.Highnote.blue4,
              color: Colors.Highnote.white,
              fontWeight: "900",
            },
            "& .MuiDataGrid-scrollbar": {
              display: "none",
            },
            "& .MuiDataGrid-scrollbarFiller": {
              display: "none",
            },
            "& .MuiDataGrid-filler": {
              display: "none",
            },
          }}
        />
      ) : (
        <h3>There is no users to select.</h3>
      )}
      <Box sx={{ mt: 5, mb: 5 }}>
        <h2>2. Type your notification message</h2>
        <TextField
          variant="outlined"
          required
          id="outlined-multiline-static"
          label="Notification Message"
          fullWidth
          multiline
          rows={4}
          error={!!notificationMessageError}
          helperText={notificationMessageError}
          value={notificationMessage}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNotificationMessage(event.target.value);
            if (event.target.value && notificationMessageError) {
              setNotificationMessageError("");
            }
          }}
          onBlur={() => {
            if (notificationMessage.length) {
              setNotificationMessageError("");
            } else {
              setNotificationMessageError(
                "The notification message is required."
              );
            }
          }}
        />
      </Box>
      <Box sx={{ mt: 5, mb: 5 }}>
        <h2>3. Select a deeplink</h2>
        <p>
          Select the Highnote app screen that should open when the user opens
          the notification.
        </p>
        <Autocomplete
          disablePortal
          id="combo-box-"
          options={mobileRouteNamesValues}
          getOptionLabel={(option) => option.label}
          clearOnEscape
          blurOnSelect
          sx={{ maxWidth: 400 }}
          value={mobileRouteNamesValues.find(option => option.value === deepLink) || null}
          onChange={(event: any, newValue: { value: string; label: string } | null) => {
            if (!newValue) {
              setDeepLink(null);
              setNotificationPayloadRequired("");
              return;
            }

            switch (newValue.value) {
              case mobileRouteNames.ViewMentorshipProgramModule:
                setNotificationPayloadRequired(
                  "Please add the mentorshipProgramProgressId value as payload."
                );
                break;

              case mobileRouteNames.ViewMessageScreen:
              case mobileRouteNames.ViewMentorshipMessageScreen:
                setNotificationPayloadRequired(
                  "Please add the messageId value as payload."
                );
                break;
              default:
                setNotificationPayloadRequired("");
                break;
            }

            setDeepLink(newValue.value);
          }}
          renderInput={(params) => <TextField {...params} label="Route Name" />}
        />
      </Box>
      {isHighnoteAdmin ? (
        <Box sx={{ mt: 5, mb: 5 }}>
          <h2>Notification Payload</h2>
          <p>
            Please enter the payload formatted as a javascript object. For
            example:
          </p>
          <pre>
            {`
            {
              "messageId": "value1", 
              "mentorshipProgramProgressId": "value2"
            }
            `}
          </pre>
          <TextField
            variant="outlined"
            id="outlined-multiline-static"
            label="Notification Payload"
            fullWidth
            multiline
            rows={4}
            required={!!notificationPayloadRequired}
            error={
              (!!notificationPayloadRequired &&
                notificationPayload.length === 0) ||
              !!notificationPayloadError
            }
            helperText={
              !!notificationPayloadRequired && notificationPayload.length === 0
                ? notificationPayloadRequired
                : notificationPayloadError
            }
            value={notificationPayload}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNotificationPayload(event.target.value);
            }}
            onBlur={() => {
              try {
                const parsedValue = JSON.parse(notificationPayload);
                setNotificationPayloadError("");
              } catch (error) {
                setNotificationPayloadError(
                  "The provided payload is not a valid javascript object."
                );
              }
            }}
          />
        </Box>
      ) : null}
      <Box sx={{ mt: 5, mb: 5 }}>
        {createSystemNotificationsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Button
            disabled={
              (!!notificationPayloadRequired &&
                notificationPayload.length === 0) ||
              notificationMessage.length === 0
            }
            onClick={handleSend}
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: Colors.Highnote.blue4,
              ":hover": {
                backgroundColor: Colors.Highnote.blue3,
                color: "white",
              },
            }}
          >
            Send
          </Button>
        )}
      </Box>

      <AlertModal
        isOpen={resultModalIsOpen}
        setIsOpen={setResultModalIsOpen}
        title="Push Notification Results"
        description={`Success Count: ${successCount}`}
        description2={`Failures Count: ${failuresCount}`}
        onPressButton2={resetState}
        button2Label="Done"
      />
    </Container>
  );
};

export default CreateNotification;